import { reactive, ref } from 'vue'
import { useApi } from '@/functions/api'
import { useAuthStore } from '@/stores/auth'

export function useTenantGetListOfCurrentUser () {
  const api = useApi()
  const loading = ref(false)

  const getData = async () => {
    loading.value = true
    try {
      const response = await api.GET<{ tenants: TenantInit[] }>('app/tenants')
      return response.tenants
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    getData,
  }
}

export function useTenantGetList () {
  const api = useApi()
  const authStore = useAuthStore()
  const loading = ref(false)
  const tenants = ref<TenantList[]>([])

  const getData = async () => {
    loading.value = true
    try {
      const response = await api.GET<ApiResponse<TenantList>>('tenant')
      tenants.value = response.data
    } finally {
      loading.value = false
    }
  }

  const created = (data: TenantList) => {
    authStore.reloadTenant()
    const index = tenants.value.findIndex((p) => p.name.localeCompare(data.name) > 0)
    if (index === -1) {
      tenants.value.push(data)
    } else {
      tenants.value.splice(index, 0, data)
    }
  }

  const edited = (data: Tenant) => {
    const index = tenants.value.findIndex((p) => p.id === data.id)
    tenants.value[index]!.name = data.name
    tenants.value.sort((a, b) => a.name.localeCompare(b.name))
    authStore.reloadTenant()
  }

  const deleted = (id: number) => {
    tenants.value = tenants.value.filter((p) => p.id !== id)
    authStore.reloadTenant()
  }

  return {
    loading,
    tenants,
    getData,
    created,
    edited,
    deleted,
  }
}

export function useTenantGetDetail () {
  const api = useApi()
  const loading = ref(false)
  const tenant = ref<TenantDetail>()

  const getData = async (id: number) => {
    loading.value = true
    try {
      const response = await api.GET<TenantDetail>(`tenant/${id}`)
      tenant.value = response
    } finally {
      loading.value = false
    }
  }

  const userCreated = (data: TenantUser) => {
    tenant.value!.users.push(data)
  }
  const userDeleted = (id: number) => {
    tenant.value!.users = tenant.value!.users.filter((p) => p.id !== id)
  }

  const roleCreated = (data: TenantRole) => {
    tenant.value!.roles.push(data)
  }
  const roleEdited = (data: TenantRoleEdited) => {
    const index = tenant.value!.roles.findIndex((p) => p.id === data.id)
    tenant.value!.roles[index]!.name = data.name
    tenant.value!.roles[index]!.updated_at = data.updated_at
  }
  const roleDeleted = (id: number) => {
    tenant.value!.roles = tenant.value!.roles.filter((p) => p.id !== id)
  }

  const rolePermissionEdited = (data: TenantRolePermissionEdited) => {
    const index = tenant.value!.roles.findIndex((p) => p.id === data.id)
    tenant.value!.roles[index]!.permissions = data.permissions
    tenant.value!.roles[index]!.updated_at = data.updated_at
  }

  return {
    loading,
    tenant,
    getData,
    roleCreated,
    roleEdited,
    roleDeleted,
    rolePermissionEdited,
    userCreated,
    userDeleted,
  }
}

export function useTenantCreateOrEdit () {
  const api = useApi()
  const submitting = ref(false)
  const errors = ref<FormError>({})
  const tenant = ref<Tenant>()
  const form = reactive({
    name: '',
  })

  const resetForm = (data?: Tenant) => {
    tenant.value = data
    errors.value = {}
    form.name = data?.name ?? ''
  }

  const create = async () => {
    submitting.value = true
    try {
      const response = await api.POST<TenantList>('tenant', form)
      return response
    } catch (error) {
      errors.value = api.formErrors(error)
    } finally {
      submitting.value = false
    }
  }

  const edit = async () => {
    submitting.value = true
    try {
      const response = await api.PUT<Tenant>(`tenant/${tenant.value!.id}`, form)
      return response
    } catch (error) {
      errors.value = api.formErrors(error)
    } finally {
      submitting.value = false
    }
  }

  return {
    submitting,
    errors,
    tenant,
    form,
    create,
    edit,
    resetForm,
  }
}

export function useTenantDelete () {
  const api = useApi()
  const submitting = ref(false)
  const tenant = ref<Tenant>()

  const resetForm = (data: Tenant) => {
    tenant.value = data
  }

  const submit = async () => {
    submitting.value = true
    try {
      await api.DELETE(`tenant/${tenant.value!.id}`)
      return true
    } catch {
      return false
    } finally {
      submitting.value = false
    }
  }

  return {
    submitting,
    tenant,
    resetForm,
    submit,
  }
}
